import { status, StatusDescriptor, worstOf } from '@/shared/ui/status.model';

export { worstOf };

/** Mirrors `DeviceRegistry.Api` `DeviceStatus` enum. */
export enum DeviceStatusCode {
    Pending = 0,
    Active = 1,
    Inactive = 2,
    Error = 3
}

/** Same threshold as `DeviceEndpoints.OfflineThreshold` (5 minutes since last check-in). */
export const OFFLINE_THRESHOLD_MS = 5 * 60 * 1000;

/** The subset of a cloud `Device` record the status derivation needs. */
export interface DeviceStatusInput {
    status?: number | null;
    lastCheckInUtc?: string | Date | null;
    lastSeenUtc?: string | Date | null;
    registrationCodeStatus?: string | null;
    registrationCodeExpired?: boolean | null;
}

/** The subset of a `LinkedDevice` record the status derivation needs. */
export interface LinkedDeviceStatusInput {
    online?: boolean | null;
    missing?: boolean | null;
    /** Driver-reported health from the last check-in; absent before the processor reports it. */
    health?: LinkedDeviceHealth | string | null;
}

/** Summary of the warnings and errors a linked device's driver reports (`ok` when there are none). */
export type LinkedDeviceHealth = 'ok' | 'warning' | 'error';

/**
 * Parse a server timestamp. Server timestamps are UTC; a string without a zone
 * suffix is treated as UTC rather than local time (avoids a DST hour error).
 */
export function parseUtc(value: string | Date | null | undefined): number | null {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value.getTime();
    let s = value;
    if (!/[Zz]|[+-]\d{2}:\d{2}$/.test(s)) s += 'Z';
    const t = new Date(s).getTime();
    return isNaN(t) ? null : t;
}

/**
 * One place that turns a control system ("System") record into a status.
 *
 * Replaces `unifiedStatus()` / `statusClass()` that used to live in the
 * dashboard component, and adds the offline rule the API uses for the hotlist:
 * an Active device whose last check-in is older than five minutes is Offline.
 */
export function deriveDeviceStatus(device: DeviceStatusInput | null | undefined, now: number = Date.now()): StatusDescriptor {
    if (!device) return status('unknown');
    const code = device.status;

    if (code === DeviceStatusCode.Active) {
        const last = parseUtc(device.lastCheckInUtc ?? device.lastSeenUtc);
        if (last !== null && now - last > OFFLINE_THRESHOLD_MS) return status('offline');
        return status('online');
    }

    if (code === DeviceStatusCode.Error) return status('warning', 'Error');

    if (code === DeviceStatusCode.Pending || code === DeviceStatusCode.Inactive) {
        if (device.registrationCodeStatus === 'expired' || device.registrationCodeExpired === true) {
            return status('expired', 'Code Expired');
        }
        if (code === DeviceStatusCode.Pending) return status('pending', 'Awaiting Adoption');
        return status('missing', 'Inactive');
    }

    return status('unknown');
}

/**
 * Status for a peripheral reported by a control system. Missing and offline come first: a driver's own
 * "offline" error must not replace the offline status. An online device with warnings or errors shows as
 * a warning, labelled "Error" for errors (as a control system in error is).
 */
export function deriveLinkedDeviceStatus(device: LinkedDeviceStatusInput | null | undefined): StatusDescriptor {
    if (!device) return status('unknown');
    if (device.missing) return status('missing');
    if (!device.online) return status('offline');
    if (device.health === 'error') return status('warning', 'Error');
    if (device.health === 'warning') return status('warning');
    return status('online');
}

/** A warning or error a linked device's driver reports, as the API returns it. */
export interface LinkedDeviceHealthMessage {
    level: 'Warning' | 'Error' | string;
    message: string;
    detailsMessage?: string;
    source?: string;
    code?: string;
    /** When the cloud first saw this message; kept while it keeps being reported. */
    firstSeenUtc?: string | null;
}

/**
 * One line naming a linked device's most important reported problem, e.g. "Camera not detected (+2 more)",
 * or null when there is nothing to call out (healthy, offline or missing - the status says those).
 */
export function linkedDeviceProblem(device: (LinkedDeviceStatusInput & { messages?: LinkedDeviceHealthMessage[] | null }) | null | undefined): string | null {
    if (!device || device.missing || !device.online) return null;
    const messages = device.messages ?? [];
    if (!messages.length) return null;
    const first = messages.find((m) => m.level === 'Error') ?? messages[0];
    return messages.length > 1 ? `${first.message} (+${messages.length - 1} more)` : first.message;
}
